import React, {useState, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {displayService, calendarService} from '../services';
import {Display, DisplayCalendar, Calendar} from '../types';
import {useSettingsNavigation} from '../hooks/useSettingsNavigation';
import {useRouting} from '../hooks/useRouting';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  SubHeading,
  BodyText,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {useDataLoader} from '../hooks/useDataLoader';

export default function DisplayCalendarsScreen() {
  const {t} = useTranslation();
  const {navigateToSettingsScreen, navigate} = useSettingsNavigation('DisplayCalendars');
  const dispatch = useAppDispatch();
  const {getRouteParam} = useRouting();
  const displayId = getRouteParam('displayId') || '';

  const [display, setDisplay] = useState<Display | null>(null);
  const [displayCalendars, setDisplayCalendars] = useState<DisplayCalendar[]>(
    [],
  );
  const [allCalendars, setAllCalendars] = useState<Calendar[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Get assigned calendar IDs for quick lookup
  const assignedCalendarIds = new Set(displayCalendars.map(dc => dc.calendarId));

  // Get available calendars (not yet assigned to this display)
  const availableCalendars = allCalendars.filter(
    c => !assignedCalendarIds.has(c.id),
  );

  const loadData = useCallback(async () => {
    const [displayData, calendarsResponse, allCalendarsData] =
      await Promise.all([
        displayService.getDisplay(displayId),
        displayService.getDisplayCalendars(displayId),
        calendarService.getUserCalendars(),
      ]);

    setDisplay(displayData);
    setDisplayCalendars(calendarsResponse.calendars);
    setAllCalendars(allCalendarsData);
  }, [displayId]);

  const {isLoading, loadError} = useDataLoader(loadData);

  const handleAddCalendar = async (calendarId: string) => {
    if (isAdding) return;
    setIsAdding(true);
    try {
      const newDisplayCalendar = await displayService.addCalendarToDisplay(
        displayId,
        {calendarId},
      );
      setDisplayCalendars(prev => [...prev, newDisplayCalendar]);
      setDisplay(prev =>
        prev ? {...prev, calendarCount: prev.calendarCount + 1} : prev,
      );
    } catch (error) {
      console.error('Failed to add calendar', error);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveCalendar = async (calendarId: string) => {
    try {
      await displayService.removeCalendarFromDisplay(displayId, calendarId);
      setDisplayCalendars(prev =>
        prev.filter(dc => dc.calendarId !== calendarId),
      );
      setDisplay(prev =>
        prev
          ? {...prev, calendarCount: Math.max(0, prev.calendarCount - 1)}
          : prev,
      );
    } catch (error) {
      console.error('Failed to remove calendar', error);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  if (isLoading && loadError !== 'network') {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </SettingsLayout>
    );
  }

  if (!display) {
    return (
      <SettingsLayout
        currentScreen="displays"
        onNavigate={navigateToSettingsScreen}
        onLogout={handleLogout}>
        <View style={styles.loadingContainer}>
          {loadError === 'network' ? (
            <>
              <OfflineBanner />
              <BodyText style={{color: theme.textMuted, textAlign: 'center', marginTop: 8}}>{t('common.offlineCannotLoad')}</BodyText>
            </>
          ) : (
            <BodyText>Display not found</BodyText>
          )}
        </View>
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout
      currentScreen="displays"
      onNavigate={navigateToSettingsScreen}
      onLogout={handleLogout}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <OfflineBanner />
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigate('DisplayEdit', {displayId})}
            activeOpacity={0.7}>
            <Text style={styles.backIcon}>arrow_back</Text>
            <Text style={styles.backText}>{display.name}</Text>
          </TouchableOpacity>
          <Heading>{t('displays.calendars')}</Heading>
          <BodyText style={styles.subtitle}>
            {t('displays.calendarsDescription')}
          </BodyText>
        </View>

        {/* Assigned Calendars */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <SubHeading>{t('displays.assignedCalendars')}</SubHeading>
            <Text style={styles.countBadge}>{displayCalendars.length}</Text>
          </View>

          {displayCalendars.length > 0 ? (
            <View style={styles.calendarList}>
              {displayCalendars.map(dc => (
                <View key={dc.id} style={styles.calendarItem}>
                  <View style={styles.calendarInfo}>
                    <View
                      style={[
                        styles.calendarColor,
                        {
                          backgroundColor: dc.calendarColor || theme.primary,
                        },
                      ]}
                    />
                    <Text style={styles.calendarName}>
                      {dc.calendarName || dc.googleCalendarId}
                    </Text>
                  </View>
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => handleRemoveCalendar(dc.calendarId)}
                    activeOpacity={0.7}>
                    <Text style={styles.removeButtonIcon}>close</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          ) : (
            <GlassPanel style={styles.emptyState}>
              <Text style={styles.emptyIcon}>calendar_month</Text>
              <BodyText style={styles.emptyText}>
                {t('displays.noCalendarsAssigned')}
              </BodyText>
              <BodyText style={styles.emptyHint}>
                {t('displays.addCalendarsHint')}
              </BodyText>
            </GlassPanel>
          )}
        </View>

        {/* Add Calendar Button */}
        {availableCalendars.length > 0 && (
          <TouchableOpacity
            style={styles.addButton}
            onPress={() => setShowAddModal(true)}
            activeOpacity={0.7}>
            <Text style={styles.addButtonIcon}>add</Text>
            <Text style={styles.addButtonText}>{t('displays.addCalendar')}</Text>
          </TouchableOpacity>
        )}

        {/* No Available Calendars Message */}
        {availableCalendars.length === 0 && allCalendars.length > 0 && (
          <GlassPanel style={styles.infoPanel}>
            <Text style={styles.infoIcon}>info</Text>
            <BodyText style={styles.infoText}>
              All your calendars are already assigned to this display.
            </BodyText>
          </GlassPanel>
        )}

        {/* No Calendars at All */}
        {allCalendars.length === 0 && (
          <GlassPanel style={styles.infoPanel}>
            <Text style={styles.infoIcon}>calendar_month</Text>
            <BodyText style={styles.infoText}>
              You haven't connected any calendars yet. Go to Calendar Settings to
              connect your Google Calendar.
            </BodyText>
            <TouchableOpacity
              style={styles.linkButton}
              onPress={() => navigate('CalendarSettings')}
              activeOpacity={0.7}>
              <Text style={styles.linkButtonText}>Go to Calendar Settings</Text>
            </TouchableOpacity>
          </GlassPanel>
        )}
      </ScrollView>

      {/* Add Calendar Modal */}
      <Modal
        visible={showAddModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAddModal(false)}>
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowAddModal(false)}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('displays.availableCalendars')}
              </Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowAddModal(false)}
                activeOpacity={0.7}>
                <Text style={styles.modalCloseIcon}>close</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.modalScrollView}>
              <View style={styles.modalCalendarList}>
                {availableCalendars.map(calendar => (
                  <TouchableOpacity
                    key={calendar.id}
                    style={styles.modalCalendarItem}
                    onPress={() => {
                      handleAddCalendar(calendar.id);
                      if (availableCalendars.length === 1) {
                        setShowAddModal(false);
                      }
                    }}
                    disabled={isAdding}
                    activeOpacity={0.7}>
                    <View style={styles.calendarInfo}>
                      <View
                        style={[
                          styles.calendarColor,
                          {
                            backgroundColor:
                              calendar.calendarColor || theme.primary,
                          },
                        ]}
                      />
                      <Text style={styles.calendarName}>
                        {calendar.calendarName || calendar.googleCalendarId}
                      </Text>
                    </View>
                    <Text style={styles.addIcon}>add</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        </Pressable>
      </Modal>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 24,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Header
  header: {
    marginBottom: 32,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 16,
  },
  backIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },
  backText: {
    color: theme.primary,
    fontSize: 16,
  },
  subtitle: {
    color: theme.textMuted,
    marginTop: 8,
  },

  // Section
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  countBadge: {
    backgroundColor: theme.primaryLight,
    color: theme.primary,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 14,
    fontWeight: '600',
  },

  // Calendar List
  calendarList: {
    gap: 8,
  },
  calendarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.surface,
    padding: 16,
    borderRadius: 12,
  },
  calendarInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  calendarColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  calendarName: {
    color: theme.text,
    fontSize: 16,
    flex: 1,
  },
  removeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeButtonIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.error,
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    padding: 32,
  },
  emptyIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 48,
    color: theme.textMuted,
    marginBottom: 16,
  },
  emptyText: {
    color: theme.text,
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyHint: {
    color: theme.textMuted,
    textAlign: 'center',
    fontSize: 14,
  },

  // Add Button
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.primary,
    paddingVertical: 14,
    borderRadius: 12,
    marginBottom: 24,
  },
  addButtonIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: '#fff',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Info Panel
  infoPanel: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 16,
    flexWrap: 'wrap',
  },
  infoIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.textMuted,
  },
  infoText: {
    flex: 1,
    color: theme.textMuted,
    fontSize: 14,
    minWidth: 200,
  },
  linkButton: {
    width: '100%',
    marginTop: 12,
    paddingVertical: 10,
    backgroundColor: theme.primaryLight,
    borderRadius: 8,
    alignItems: 'center',
  },
  linkButtonText: {
    color: theme.primary,
    fontSize: 14,
    fontWeight: '500',
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.surface,
    borderRadius: 16,
    width: '90%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  modalTitle: {
    color: theme.text,
    fontSize: 18,
    fontWeight: '600',
  },
  modalCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalCloseIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.textMuted,
  },
  modalScrollView: {
    maxHeight: 400,
  },
  modalCalendarList: {
    padding: 16,
    gap: 8,
  },
  modalCalendarItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: 16,
    borderRadius: 12,
  },
  addIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.primary,
  },
});
